import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { logoFor } from "../lib/brand";
import { Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
      <Toaster position="top-center" richColors />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60 dark:opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 20%, hsl(var(--secondary)) 0%, transparent 45%), radial-gradient(circle at 20% 80%, hsl(var(--secondary)) 0%, transparent 50%)",
        }}
      />
      <div aria-hidden className="absolute inset-0 grain pointer-events-none" />

      <div
        data-testid="register-card"
        className="relative w-full max-w-md bg-card/95 backdrop-blur-2xl border border-border/60 shadow-glass rounded-[24px] p-8 md:p-10 animate-fade-up"
      >
        <div className="flex items-center mb-6">
          <img
            src={logoFor(theme)}
            alt="SK Kapoor — GPT Hub"
            className="h-12 w-auto object-contain select-none"
            draggable={false}
          />
        </div>

        <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-bold">
          Join the Hub.
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Create your operator account to get started.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Name
            </label>
            <input
              data-testid="register-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full h-12 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10 focus:border-foreground/40 transition"
              placeholder="Jane Operator"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Email
            </label>
            <input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full h-12 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10 focus:border-foreground/40 transition"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Password
            </label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full h-12 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10 focus:border-foreground/40 transition"
              placeholder="At least 6 characters"
            />
          </div>
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-brand text-brand-foreground font-medium tracking-wide flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-60 shadow-[0_8px_24px_-6px_hsl(var(--brand-accent)/0.55)]"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Create account
          </button>
        </form>

        <div className="mt-6 text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="link-login"
            className="text-foreground underline underline-offset-4 hover:opacity-80"
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
